import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Send,
  Users,
  Mail,
  Phone,
  MessageSquare,
  X,
  Search,
  Check
} from 'lucide-react';
import { communicationService, patientService } from '../../services/api';
import { MessageTemplate, Patient } from '../../types';

export default function SendMessage() {
  const [selectedPatients, setSelectedPatients] = useState<Patient[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [communicationType, setCommunicationType] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [useTemplate, setUseTemplate] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPatientSelector, setShowPatientSelector] = useState(false);

  // Récupérer les patients
  const { data: patientsData } = useQuery({
    queryKey: ['patients'],
    queryFn: () => patientService.getAll().then(res => res.data)
  });

  // Récupérer les templates
  const { data: templatesData } = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => communicationService.getTemplates().then(res => res.data)
  });

  const patients = patientsData?.results || [];
  const templates = templatesData?.results || [];

  // Filtrer les patients selon la recherche
  const filteredPatients = patients.filter(patient =>
    `${patient.nom} ${patient.prenoms}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.telephone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Mutation pour envoyer le message
  const sendMessageMutation = useMutation({
    mutationFn: (data: any) => communicationService.sendMessage(data),
    onSuccess: () => {
      alert('Messages envoyés avec succès!');
      // Reset form
      setSelectedPatients([]);
      setSelectedTemplate(null);
      setCustomSubject('');
      setCustomMessage('');
    },
    onError: (error) => {
      console.error('Erreur envoi message:', error);
      alert('Erreur lors de l\'envoi des messages');
    }
  });

  // Mutation pour envoi en masse
  const sendBulkMessageMutation = useMutation({
    mutationFn: (data: any) => communicationService.sendBulkMessage(data),
    onSuccess: () => {
      alert('Messages envoyés en masse avec succès!');
      setSelectedPatients([]);
    },
    onError: (error) => {
      console.error('Erreur envoi en masse:', error);
      alert('Erreur lors de l\'envoi en masse');
    }
  });

  const handlePatientToggle = (patient: Patient) => {
    setSelectedPatients(prev => {
      const exists = prev.find(p => p.id === patient.id);
      if (exists) {
        return prev.filter(p => p.id !== patient.id);
      } else {
        return [...prev, patient];
      }
    });
  };

  const handleSelectAllPatients = () => {
    setSelectedPatients(filteredPatients);
  };

  const handleClearSelection = () => {
    setSelectedPatients([]);
  };

  const handleSendMessage = () => {
    if (selectedPatients.length === 0) {
      alert('Veuillez sélectionner au moins un patient');
      return;
    }

    if (useTemplate && !selectedTemplate) {
      alert('Veuillez sélectionner un template');
      return;
    }

    if (!useTemplate && (!customSubject || !customMessage)) {
      alert('Veuillez remplir le sujet et le message');
      return;
    }

    const messageData = {
      patient_ids: selectedPatients.map(p => p.id),
      communication_type: communicationType,
      ...(useTemplate ? {
        template_id: selectedTemplate?.id
      } : {
        subject: customSubject,
        message: customMessage
      })
    };

    if (selectedPatients.length === 1) {
      sendMessageMutation.mutate({
        patient_id: selectedPatients[0].id,
        ...messageData
      });
    } else {
      sendBulkMessageMutation.mutate(messageData);
    }
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'sms':
        return <Phone className="h-4 w-4" />;
      case 'whatsapp':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Envoyer un message</h2>
        <p className="text-gray-600">Envoyez des messages personnalisés ou basés sur des templates</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sélection des destinataires */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Destinataires</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedPatients.length} sélectionné(s)
                </span>
                {selectedPatients.length > 0 && (
                  <button
                    onClick={handleClearSelection}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Actions rapides */}
            <div className="flex space-x-2 mb-4">
              <button
                onClick={handleSelectAllPatients}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                Sélectionner tout
              </button>
              <button
                onClick={handleClearSelection}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Désélectionner tout
              </button>
            </div>

            {/* Liste des patients */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredPatients.map((patient) => (
                <div
                  key={patient.id}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedPatients.find(p => p.id === patient.id)
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                  onClick={() => handlePatientToggle(patient)}
                >
                  <div className="flex-shrink-0 mr-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedPatients.find(p => p.id === patient.id)
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedPatients.find(p => p.id === patient.id) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {patient.nom} {patient.prenoms}
                    </div>
                    <div className="text-sm text-gray-500">
                      {patient.email} • {patient.telephone}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Configuration du message */}
        <div className="space-y-6">
          {/* Type de communication */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Canal de communication</h3>
            
            <div className="space-y-2">
              {[
                { type: 'email', label: 'Email', icon: Mail, color: 'blue' },
                { type: 'sms', label: 'SMS', icon: Phone, color: 'purple' },
                { type: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'green' }
              ].map(({ type, label, icon: Icon, color }) => (
                <label key={type} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="communicationType"
                    value={type}
                    checked={communicationType === type}
                    onChange={(e) => setCommunicationType(e.target.value as any)}
                    className="sr-only"
                  />
                  <div className={`flex items-center p-3 w-full rounded-lg border-2 transition-colors ${
                    communicationType === type
                      ? `bg-${color}-50 border-${color}-200`
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}>
                    <Icon className={`h-5 w-5 mr-3 ${communicationType === type ? `text-${color}-600` : 'text-gray-500'}`} />
                    <span className={`font-medium ${communicationType === type ? `text-${color}-900` : 'text-gray-700'}`}>
                      {label}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Mode de composition */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Mode de composition</h3>
            
            <div className="space-y-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="messageMode"
                  checked={useTemplate}
                  onChange={() => setUseTemplate(true)}
                  className="mr-3"
                />
                <span>Utiliser un template</span>
              </label>
              
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="messageMode"
                  checked={!useTemplate}
                  onChange={() => setUseTemplate(false)}
                  className="mr-3"
                />
                <span>Message personnalisé</span>
              </label>
            </div>

            {useTemplate ? (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner un template
                </label>
                <select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === parseInt(e.target.value));
                    setSelectedTemplate(template || null);
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Choisir un template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet
                  </label>
                  <input
                    type="text"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Sujet du message"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Contenu du message"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Bouton d'envoi */}
          <button
            onClick={handleSendMessage}
            disabled={sendMessageMutation.isLoading || sendBulkMessageMutation.isLoading}
            className="w-full flex items-center justify-center px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {(sendMessageMutation.isLoading || sendBulkMessageMutation.isLoading) ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Envoyer le message
          </button>
        </div>
      </div>
    </div>
  );
}